import { createRootRoute, Outlet, useMatches } from "@tanstack/react-router";
import { GeneralHeader } from "@/components/shared/GeneralHeader";
import { Sidebar } from "@/components/shared/sidebar/Sidebar";
import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { getProvider } from "@/app/store/provider/providerSlice";
import { getItemSessionStorage } from "@/lib/utils";
import { ELocalStorageKeys } from "@/app/types/localStorage";
import { ShareAccessShell } from "@/components/share/ShareAccessShell";

const RootLayout = () => {
  const matches = useMatches();
  const pathname = matches[matches.length - 1].pathname;
  const isShareRoute =
    pathname === "/share" ||
    pathname.startsWith("/share/");
  const isLogin = pathname.includes("/login/");
  const isResetPassword = pathname.includes("/reset-password/");
  const isBetaRegister = pathname.includes("/register/beta/");
  const isSelfServiceRegister = pathname === "/register" || pathname === "/register/";
  const isPlusOnboarding =
    pathname === "/register/onboarding" ||
    pathname === "/register/onboarding/";
  const isCheckoutRegister = pathname === "/register/checkout" || pathname === "/register/checkout/";
  const isSuccessMessage = pathname.includes("/success/change-password/");
  const isDisabledUser = pathname.includes("/error/disabled");
  const isAuthRoute =
    isLogin ||
    isResetPassword ||
    isBetaRegister ||
    isSelfServiceRegister ||
    isCheckoutRegister ||
    isPlusOnboarding ||
    isSuccessMessage ||
    isDisabledUser;
  const token = getItemSessionStorage(ELocalStorageKeys.TOKEN);
  const dispatch = useAppDispatch();
  const providerStatus = useAppSelector((state) => state.provider.status);

  useEffect(() => {
    if (
      token &&
      !isAuthRoute &&
      !isShareRoute &&
      providerStatus === "idle"
    ) {
      dispatch(getProvider());
    }
  }, [
    dispatch,
    token,
    isAuthRoute,
    isShareRoute,
    providerStatus,
  ]);

  if (isShareRoute) {
    return <ShareAccessShell />;
  }

  if (isAuthRoute) {
    return (
      <div className="min-h-screen bg-beige mx-auto overflow-y-hidden">
        <main className="max-w-[1920px] max-h-screen bg-beige mx-auto overflow-y-hidden">
          <Outlet />
        </main>
      </div>
    );
  }

  return (
    <div className="bg-[#FBFAF8] mx-auto">
      <div className="max-h-screen flex flex-col bg-[#FBFAF8] max-w-[1920px] mx-auto">
        <GeneralHeader />
        <div className="flex flex-1 overflow-hidden">
          <Sidebar />
          <main
            className="flex-1 p-8 bg-[#FBFAF8] min-h-screen overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:rounded-full [&::-webkit-scrollbar-track]:bg-[#FBFAF8] [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-neutral-100"
          >
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
};

export const Route = createRootRoute({
  component: RootLayout,
});
